import { computed, ref } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { router, usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import IBanner, { BannerKey } from '@/types/Banners/IBanner';

const shouldDisplayBlackFridayDeal = ref(false);
const shouldDisplayLettrPromoTemplateCard = ref(false);
const shouldDisplayLettrPromoBanner = ref(false);
const shouldDisplayLettrPromoSidebar = ref(false);

/**
 * Banners dismissed in this tab, keyed per team, so they disappear at once.
 * Each entry lives only until the server's list - which applies the stored
 * dismissal - has been reloaded, so a later block brings the banner back.
 */
const dismissedBanners = ref<Set<string>>(new Set());

export const useBanners = () => {
    const page = usePage<ITopolPageProps>();

    const dismissalKey = (key: BannerKey, teamId = page.props.team?.id) =>
        `${teamId}:${key}`;

    /**
     * The top-of-page banners, already filtered and ordered by the backend
     * resolver (App\Topol\Banners\Builders\BannerResolver).
     */
    const banners = computed<IBanner[]>(() =>
        (page.props.banners ?? []).filter(
            (banner) =>
                !dismissedBanners.value.has(
                    dismissalKey(banner.key, banner.dismissal?.teamId)
                )
        )
    );

    /**
     * Banners are shared once and cached by the client, so anything that
     * changes them (subscribing, paying, ...) has to ask for them again.
     * Async so it neither cancels nor gets cancelled by the caller's own reload.
     */
    const reloadBanners = () =>
        router.reload({ only: ['banners'], async: true });

    /**
     * Hides the banner straight away, then swaps the optimistic hide for the
     * server's answer; a failed request just brings the banner back. Sends
     * the team and block the viewer saw, so the server never dismisses a
     * block on another team or one that appeared after the banner rendered.
     */
    const dismissBanner = async (banner: IBanner) => {
        if (!banner.dismissal) {
            return;
        }

        const localKey = dismissalKey(banner.key, banner.dismissal.teamId);
        dismissedBanners.value = new Set(dismissedBanners.value).add(localKey);

        try {
            await axios.put(route('users.banner-dismissals.update'), {
                banner: banner.key,
                team_id: banner.dismissal.teamId,
                cycle: banner.dismissal.cycle,
            });
        } catch (err) {
            // Reloading below restores the banner the server still shows.
        }

        router.reload({
            only: ['banners'],
            async: true,
            onFinish: () => {
                const next = new Set(dismissedBanners.value);
                next.delete(localKey);
                dismissedBanners.value = next;
            },
        });
    };

    /**
     * Promo placements (Lettr, Black Friday) are not top-of-page banners and
     * still come from the banners endpoint.
     */
    const fetchBanners = async () => {
        try {
            const res = await axios.get(route('user.banners.index'));

            if (res.data.success) {
                shouldDisplayBlackFridayDeal.value =
                    res.data.data.shouldDisplayBlackFridayDeal;
                shouldDisplayLettrPromoTemplateCard.value =
                    res.data.data.lettrPromoTemplateCard;
                shouldDisplayLettrPromoBanner.value =
                    res.data.data.lettrPromoBanner;
                shouldDisplayLettrPromoSidebar.value =
                    res.data.data.lettrPromoSidebar;
            }
        } catch (err) {
            return;
        }
    };

    return {
        banners,
        reloadBanners,
        dismissBanner,
        fetchBanners,
        shouldDisplayBlackFridayDeal,
        shouldDisplayLettrPromoTemplateCard,
        shouldDisplayLettrPromoBanner,
        shouldDisplayLettrPromoSidebar,
    };
};

export default useBanners;
